/** @format */
import { TFunction } from 'next-i18next';

import { KitIntelGenre } from '@toucan/common/features/beta-model-insights/kit-intel';

/**
 * The export's `value` is pre-composed prose ("49%", "Nearly all", "3") and
 * is not guaranteed to be a percentage, so this only recognizes the one
 * shape it needs to treat specially: a value ending in `%`. Anything else
 * comes back whole in `digits` with `unit` left `null`.
 */
export function splitLeadValue(value: string): {
    digits: string;
    unit: string | null;
} {
    if (!value.endsWith('%')) {
        return { digits: value, unit: null };
    }

    return { digits: value.slice(0, -1), unit: '%' };
}

/**
 * The reader-facing name of a genre.
 *
 * The labels are short summaries of the export's own card headings — "Bridge the
 * mount" is filed under `adapter` and shown as "Adapters", "Also considered" keeps
 * its own words — since a pill has room for a word or two where a heading has a
 * line.
 *
 * A switch of literal `t()` calls rather than a lookup through a map of English
 * phrases: `t(SOME_MAP[genre])` reads the same at runtime, but the translation
 * scanner only sees strings written literally at a `t()` call, so keys reached
 * through a map are pruned from the message files as unused on the next
 * extraction — which is exactly what happened to these.
 */
// eslint-disable-next-line id-length -- Legacy code: do not use as an example.
export function getGenreLabel(t: TFunction, genre: KitIntelGenre): string {
    switch (genre) {
        case 'travel':
            return t('Travel');
        case 'portrait':
            return t('Portrait');
        case 'events':
            return t('Events');
        case 'wildlife':
            return t('Wildlife');
        case 'everyday':
            return t('Everyday');
        case 'macro':
            return t('Macro');
        case 'reach':
            return t('Reach');
        case 'adapter':
            return t('Adapters');
        case 'bodies':
            return t('Bodies');
        case 'power':
            return t('Power');
        case 'protection':
            return t('Protection');
        case 'storage':
            return t('Storage');
        case 'extras':
            return t('Extras');
        case 'considered':
            return t('Also considered');
        case 'accessory':
            return t('Accessory');
        default: {
            /*
             * The genre is narrowed to the closed set server-side, so there is
             * nothing left for this to be — asserting that here means adding a
             * genre to the type is a compile error until it is named above.
             */
            const unhandled: never = genre;

            return unhandled;
        }
    }
}
